import type { RedoCart } from "@redotech/merchant-sdk/rpc/schema/redo-supplies/redo-cart";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import type {
  LocationId,
  ProductFamilyId,
  ProductId,
} from "@redotech/redo-model/ids";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

export interface RedoSuppliesCartContextValue {
  cart: RedoCart | null;
  isLoading: boolean;
  isCartOpen: boolean;
  setCartOpen: (open: boolean) => void;
  refreshCart: () => void;
  upsertItem: (
    productId: ProductId,
    productFamilyId: ProductFamilyId,
    quantityDelta: number,
    productInfo: {
      title: string;
      price: number;
      currency: string;
      imageUrl: string | null;
      options: { name: string; value: string }[];
      attributes?: { key: string; value: string }[];
    },
  ) => Promise<void>;
  setDestinationLocation: (locationId: LocationId | null) => Promise<void>;
}

const RedoSuppliesCartContext =
  createContext<RedoSuppliesCartContextValue | null>(null);

const CART_QUERY_KEY = ["redo-supplies-cart"];

export function useRedoSuppliesCart(): RedoSuppliesCartContextValue {
  const ctx = useContext(RedoSuppliesCartContext);
  if (!ctx) {
    throw new Error(
      "useRedoSuppliesCart must be used within a RedoSuppliesCartProvider",
    );
  }
  return ctx;
}

export const RedoSuppliesCartProvider = memo(function RedoSuppliesCartProvider({
  children,
}: {
  children: ReactNode;
}) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const queryClient = useQueryClient();
  const [isCartOpen, setCartOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: CART_QUERY_KEY,
    queryFn: () => rpcClient.getRedoCart({}),
  });

  const refreshCart = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
  }, [queryClient]);

  const upsertItem = useCallback(
    async (
      productId: ProductId,
      productFamilyId: ProductFamilyId,
      quantityDelta: number,
      productInfo: {
        title: string;
        price: number;
        currency: string;
        imageUrl: string | null;
        options: { name: string; value: string }[];
        attributes?: { key: string; value: string }[];
      },
    ) => {
      const result = await rpcClient.upsertRedoCartItems({
        productId,
        productFamilyId,
        quantityDelta,
        ...productInfo,
      });
      // Set data for instant UI update, then invalidate to converge on
      // true server state in case concurrent requests resolve out of order.
      queryClient.setQueryData(CART_QUERY_KEY, result);
      void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
    },
    [rpcClient, queryClient],
  );

  const setDestinationLocation = useCallback(
    async (locationId: LocationId | null) => {
      const result = await rpcClient.setCartDestinationLocation({ locationId });
      queryClient.setQueryData(CART_QUERY_KEY, result);
      void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
    },
    [rpcClient, queryClient],
  );

  const value: RedoSuppliesCartContextValue = {
    cart: data?.cart ?? null,
    isLoading,
    isCartOpen,
    setCartOpen,
    refreshCart,
    upsertItem,
    setDestinationLocation,
  };

  return (
    <RedoSuppliesCartContext.Provider value={value}>
      {children}
    </RedoSuppliesCartContext.Provider>
  );
});
